
import { defineComponent, ref, onMounted, computed } from "vue";
import { useStore } from "vuex";
import { useI18n } from "vue-i18n";
import { Actions } from "@/store/enums/StoreEnums";
import moment from "moment";
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb";
import { ShippingRecord } from "@/store/modules/ShippingRecordModule";
import Swal from "sweetalert2/dist/sweetalert2.min.js";

export default defineComponent({
  name: "shipping-records-listing",
  components: {},
  setup() {
    const { t } = useI18n();
    const store = useStore();
    const searchQuery = ref({
      user_mobile: "",
      shipping_no: "",
      product_name: ""
    });

    // el-date-picker daterange 返回数组 [from, to] 或 null(clearable);提交时从这里直接派生 params
    const dateRange = ref<[string, string] | null>(null);

    const page = ref({
      currentPage: 1,
      pageSize: 10,
      totalResult: 0
    });

    const tableData = ref<ShippingRecord[]>([]);
    const loading = computed(() => store.state.ShippingRecordModule.loading);

    const editingId = ref<number | null>(null);
    const editForm = ref({
      shipping_provider: "",
      shipping_no: ""
    });

    const dateRangeParams = () => {
      if (dateRange.value && dateRange.value.length === 2) {
        return { date_from: dateRange.value[0], date_to: dateRange.value[1] };
      }
      return { date_from: "", date_to: "" };
    };

    const loadData = () => {
      const params: any = {
        currentPage: page.value.currentPage,
        pageSize: page.value.pageSize,
        user_mobile: searchQuery.value.user_mobile,
        shipping_no: searchQuery.value.shipping_no,
        product_name: searchQuery.value.product_name,
        ...dateRangeParams()
      };

      store.dispatch(Actions.FETCH_SHIPPING_RECORDS, params)
        .then(() => {
          tableData.value = store.getters.currentShippingRecords;
          page.value.totalResult = store.getters.currentShippingRecordCount;
        })
        .catch((errorData) => {
          Swal.fire({
            text: errorData?.error_msg || '加载失败',
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: t("confirm"),
            customClass: { confirmButton: "btn fw-bold btn-light-danger" },
          });
        });
    };

    const handleSearch = () => {
      page.value.currentPage = 1;
      loadData();
    };

    const onPageChange = (evt: any) => {
      const { type, currentPage, pageSize } = evt;
      
      if (type === 'size') {
        page.value.currentPage = 1;
      } else {
        page.value.currentPage = currentPage;
      }
      
      page.value.pageSize = pageSize;
      loadData();
    };

    const formatDate = (date: string) => {
      return moment(date).format("YYYY-MM-DD HH:mm:ss");
    };

    const startEdit = (record: ShippingRecord) => {
      editingId.value = record.id;
      editForm.value = {
        shipping_provider: record.shipping_provider,
        shipping_no: record.shipping_no
      };
    };

    const cancelEdit = () => {
      editingId.value = null;
      editForm.value = {
        shipping_provider: "",
        shipping_no: ""
      };
    };

    const handleSave = async (record: ShippingRecord) => {
      if (!editForm.value.shipping_provider.trim()) {
        Swal.fire({
          text: t("shipping_provider_required"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("confirm"),
          customClass: {
            confirmButton: "btn fw-bold btn-light-primary",
          },
        });
        return;
      }
      
      if (!editForm.value.shipping_no.trim()) {
        Swal.fire({
          text: t("shipping_no_required"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("confirm"),
          customClass: {
            confirmButton: "btn fw-bold btn-light-primary",
          },
        });
        return;
      }

      try {
        await store.dispatch(Actions.UPDATE_SHIPPING_RECORD, {
          id: record.id,
          shipping_provider: editForm.value.shipping_provider.trim(),
          shipping_no: editForm.value.shipping_no.trim()
        });
        
        Swal.fire({
          text: t("update_success"),
          icon: "success",
          buttonsStyling: false,
          confirmButtonText: t("confirm"),
          customClass: {
            confirmButton: "btn fw-bold btn-light-primary",
          },
        });
        
        loadData();
        cancelEdit();
      } catch (error) {
        Swal.fire({
          text: t("update_fail"),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t("confirm"),
          customClass: {
            confirmButton: "btn fw-bold btn-light-primary",
          },
        });
      }
    };

    const SHIPPING_RECORDS_MAX_RANGE_DAYS = 31;

    const onDownload = async () => {
      // 后端强制日期范围(≤31 天)防全量导出击穿内存。发请求前先校验,
      // 直接告诉用户怎么做,避免无谓的 422 + 含糊的 download_fail 提示。
      const range = dateRangeParams();
      if (!range.date_from || !range.date_to) {
        Swal.fire({
          text: t("please_select_date_range", { days: SHIPPING_RECORDS_MAX_RANGE_DAYS }),
          icon: "warning",
          buttonsStyling: false,
          confirmButtonText: t("confirm"),
          customClass: { confirmButton: "btn fw-bold btn-light-primary" },
        });
        return;
      }
      const fromDate = new Date(range.date_from);
      const toDate = new Date(range.date_to);
      const spanDays = Math.round((toDate.getTime() - fromDate.getTime()) / 86400000);
      if (spanDays < 0 || spanDays > SHIPPING_RECORDS_MAX_RANGE_DAYS) {
        Swal.fire({
          text: t("date_range_too_large", { days: SHIPPING_RECORDS_MAX_RANGE_DAYS }),
          icon: "warning",
          buttonsStyling: false,
          confirmButtonText: t("confirm"),
          customClass: { confirmButton: "btn fw-bold btn-light-primary" },
        });
        return;
      }

      try {
        const params = {
          user_mobile: searchQuery.value.user_mobile,
          shipping_no: searchQuery.value.shipping_no,
          product_name: searchQuery.value.product_name,
          ...range
        };

        const response = await store.dispatch(Actions.DOWNLOAD_SHIPPING_RECORDS, params);
        
        // 创建临时链接下载文件
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'shipping_records.xlsx');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

      } catch (error: any) {
        Swal.fire({
          text: error?.error_msg || t('download_fail'),
          icon: "error",
          buttonsStyling: false,
          confirmButtonText: t('confirm'),
          customClass: {
            confirmButton: "btn fw-bold btn-light-danger",
          },
        });
      }
    };

    const handleReturn = async (record: ShippingRecord) => {
      const result = await Swal.fire({
        text: t("confirm_return"),
        icon: "warning",
        showCancelButton: true,
        buttonsStyling: false,
        confirmButtonText: t("confirm"),
        cancelButtonText: t("cancel"),
        customClass: {
          confirmButton: "btn fw-bold btn-light-primary me-3",
          cancelButton: "btn fw-bold btn-light",
        },
      });

      if (result.isConfirmed) {
        try {
          await store.dispatch(Actions.RETURN_SHIPPING_RECORD, record.id);
          
          Swal.fire({
            text: t("return_success"),
            icon: "success",
            buttonsStyling: false,
            confirmButtonText: t("confirm"),
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          });
          
          loadData();
        } catch (error) {
          Swal.fire({
            text: t("return_fail"),
            icon: "error",
            buttonsStyling: false,
            confirmButtonText: t("confirm"),
            customClass: {
              confirmButton: "btn fw-bold btn-light-primary",
            },
          });
        }
      }
    };

    onMounted(() => {
      setCurrentPageBreadcrumbs("发货记录", ["发货管理"]);
      loadData();
    });

    const formatPrice = (price: number | string | null | undefined): string => {
      if (price === null || price === undefined) return '0.0'
      const num = Number(price)
      return isNaN(num) ? '0.0' : num.toFixed(1)
    }

    return {
      tableData,
      loading,
      searchQuery,
      dateRange,
      page,
      handleSearch,
      onPageChange,
      formatDate,
      editingId,
      editForm,
      startEdit,
      cancelEdit,
      handleSave,
      onDownload,
      handleReturn,
      formatPrice,
    };
  },
});
